import { error } from "console";
import { WaveformPlayheadPositionConversionType } from "./actions";
import { HourBasedSegmentViewModel, IHourBasedSegmentViewModel, WaveformType } from "./interfaces/state";

export interface Positions {
    processedStart: number[],
    processedEnd: number[],
    sensoredProcessedStart: number[],
    sensoredProcessedEnd: number[],
    rawDuration: number
}
export interface EventMappingModel {
    rawStart?: number | null;
    rawEnd?: number | null;
    processedStart?: number | null;
    processedEnd?: number | null;
    sensorProcessedStart?: number | null;
    sensorProcessedEnd?: number | null;
}

const SECONDS_IN_HOUR = 3600;
const DEFAULT_RAW_DURATION_HRS = 24;
const SAMPLE_RATE = 8000;

const SampleToSecond = (sample: number) => sample / SAMPLE_RATE;
const SecondToSample = (second: number) => (second * SAMPLE_RATE);
const WithinRange = (value: number, start: number, end: number) => value >= start && value <= end;


const MapSampledPoint = (start: number[], end: number[], samplePoint: number, mapToRaw: boolean) => {
    return mapToRaw
        ? MapSampledPointToRaw(start, end, samplePoint)
        : MapSampledPointToProcessed(start, end, samplePoint);
}



const MapSampledPointToProcessed = (start: number[], end: number[], samplePoint: number) => {
    let mappedSamplePoint = 0;
    let processedStart = 0;
    let prevProcessEnd = 0;
    let prevRawEnd = 0;

    for (let index = 0; index < start.length; index++) {
        const rawStart = start[index];
        const rawEnd = end[index];

        const mappedLength = rawEnd - rawStart;
        processedStart++;
        const processedEnd = processedStart + mappedLength;


        if (WithinRange(samplePoint, rawStart, rawEnd))
            mappedSamplePoint = processedStart + (samplePoint - rawStart);
        else if (mappedSamplePoint == 0 && (rawStart == samplePoint))
            mappedSamplePoint = processedStart;

        if (samplePoint < rawStart) {
            // NOTE: if not mapped find the nearest chunk
            if (mappedSamplePoint == 0) {
                const leftChunkDistance = samplePoint - prevRawEnd;
                const rightChunkDistance = rawStart - samplePoint;

                mappedSamplePoint = leftChunkDistance <= rightChunkDistance
                    ? prevProcessEnd
                    : processedStart;
            }

            break;
        }

        prevProcessEnd = processedEnd;
        prevRawEnd = rawEnd;

        processedStart = processedEnd;
    }


    return mappedSamplePoint;
}

const MapSampledPointToRaw = (start: number[], end: number[], samplePoint: number) => {
    let mappedSamplePoint = 0;
    let processedStart = 0;
    let prevRawStart = 0;
    let prevProcessedEnd = 0;

    for (let index = 0; index < start.length; index++) {
        const rawStart = start[index];
        const rawEnd = end[index];

        const mappedLength = rawEnd - rawStart;
        processedStart++;
        const processedEnd = processedStart + mappedLength;


        if (WithinRange(samplePoint, processedStart, processedEnd))
            mappedSamplePoint = rawStart + (samplePoint - processedStart);

        if (samplePoint < processedStart) {
            // NOTE: if not mapped find the nearest chunk
            // WARNING: theoretically should never happen
            if (mappedSamplePoint == 0) {
                const leftChunkDistance = samplePoint - prevProcessedEnd;
                const rightChunkDistance = processedStart - samplePoint;

                mappedSamplePoint = leftChunkDistance <= rightChunkDistance
                    ? prevProcessedEnd
                    : processedStart;
            }

            break;
        }

        prevRawStart = rawStart;
        prevProcessedEnd = processedEnd;

        processedStart = processedEnd;
    }
    return mappedSamplePoint;
}

const MapSampledPeriod = (
    start: number[],
    end: number[],
    samplePeriodStart: number,
    samplePeriodEnd: number,
    mapToRaw: boolean) => {
    return mapToRaw
        ? MapSampledPeriodToRaw(start, end, samplePeriodStart, samplePeriodEnd)
        : MapSampledPeriodToProcessed(start, end, samplePeriodStart, samplePeriodEnd);
}

const MapSampledPeriodToRaw = (
    start: number[],
    end: number[],
    samplePeriodStart: number,
    samplePeriodEnd: number) => {
    let mappedStart = 0;
    let mappedEnd = 0;
    let processedStart = 0;

    for (let index = 0; index < start.length; index++) {
        const rawStart = start[index];
        const rawEnd = end[index];

        const mappedLength = rawEnd - rawStart;
        processedStart++;
        const processedEnd = processedStart + mappedLength;


        if (WithinRange(samplePeriodStart, processedStart, processedEnd))
            mappedStart = rawStart + (samplePeriodStart - processedStart);

        if (WithinRange(samplePeriodEnd, processedStart, processedEnd))
            mappedEnd = rawStart + (samplePeriodEnd - processedStart);

        if (samplePeriodEnd < processedStart)
            break;

        processedStart = processedEnd;
    }

    return { mappedStart, mappedEnd };
}

const MapSampledPeriodToProcessed = (start: number[],
    end: number[],
    samplePeriodStart: number,
    samplePeriodEnd: number) => {
    let mappedStart = 0;
    let mappedEnd = 0;
    let processedStart = 0;

    for (let index = 0; index < start.length; index++) {
        const rawStart = start[index];
        const rawEnd = end[index];

        const mappedLength = rawEnd - rawStart;
        processedStart++;
        const processedEnd = processedStart + mappedLength;


        if (WithinRange(samplePeriodStart, rawStart, rawEnd))
            mappedStart = processedStart + (samplePeriodStart - rawStart);
        else if (mappedStart == 0 && WithinRange(rawStart, samplePeriodStart, samplePeriodEnd))
            mappedStart = processedStart;

        if (mappedStart != 0 && WithinRange(samplePeriodEnd, rawStart, rawEnd))
            mappedEnd = processedStart + (samplePeriodEnd - rawStart);
        else if (mappedStart != 0 && rawEnd <= samplePeriodEnd)
            mappedEnd = processedEnd;

        if (samplePeriodEnd < rawStart)
            break;

        processedStart = processedEnd;
    }

    return { mappedStart, mappedEnd };
}

const InternalMapPeriod = (
    start: number[],
    end: number[],
    startSecond: number,
    endSecond: number,
    mapToRaw: boolean) => {
    if (startSecond > endSecond)
        throw new Error("Start time cannot be after end time.");

    if (!start && !end)
        return {
            start: 0,
            end: 0
        };


    const { mappedStart, mappedEnd } =
        MapSampledPeriod(
            start,
            end,
            SecondToSample(startSecond),
            SecondToSample(endSecond),
            mapToRaw);

    return {
        start: SampleToSecond(mappedStart),
        end: SampleToSecond(mappedEnd)
    };

}
export function MapPeriod(
    positions: Positions,
    rawStart: number,
    rawEnd: number,
    processedStart: number,
    processedEnd: number,
    sensorProcessedStart: number,
    sensorProcessedEnd: number): EventMappingModel {

    const result = {
        rawStart,
        rawEnd,
        processedStart,
        processedEnd,
        sensorProcessedStart,
        sensorProcessedEnd
    }

    if (!positions)
        return result;

    // map from Processed to Raw
    if (processedStart) {
        const rawResult = InternalMapPeriod(
            positions.processedStart,
            positions.processedEnd,
            processedStart,
            processedEnd ?? processedStart,
            true);

        result.rawStart = rawResult.start;
        result.rawEnd = rawResult.end;
    }
    // map from Sensor Processed Raw
    else if (sensorProcessedStart != null) {
        const rawResult =
            InternalMapPeriod(
                positions.sensoredProcessedStart,
                positions.sensoredProcessedEnd,
                sensorProcessedStart,
                sensorProcessedEnd ?? sensorProcessedStart,
                true);
        result.rawStart = rawResult.start;
        result.rawEnd = rawResult.end;
    }
    // map from Raw to Sensor Processed
    if (processedStart == null
        && positions.processedStart != null
        && positions.processedEnd != null) {
        const processedResult =
            InternalMapPeriod(
                positions.processedStart,
                positions.processedEnd,
                rawStart,
                rawEnd ?? rawStart,
                false);
        result.processedStart = processedResult.start;
        result.processedEnd = processedResult.end;
    }
    // map from Raw to Processed
    if (sensorProcessedStart == null
        && positions.sensoredProcessedStart != null
        && positions.sensoredProcessedEnd != null) {
        const sensorProcessedResult =
            InternalMapPeriod(
                positions.sensoredProcessedStart,
                positions.sensoredProcessedEnd,
                rawStart,
                rawEnd ?? rawStart,
                false)

        result.sensorProcessedStart = sensorProcessedResult.start;
        result.sensorProcessedEnd = sensorProcessedResult.end;
    }
    return result;
}

const InternalMapPoint = (start: number[], end: number[], timestamp: number, mapToRaw: boolean) => {
    if (timestamp < 0)
        throw Error(`Argument 'timestamp' can not have a value less than 0.`);

    return SampleToSecond(
        MapSampledPoint(
            start,
            end,
            SecondToSample(timestamp),
            mapToRaw)
    );
}

export function MapPoint(positions: Positions, conversionType, timestamp: number) {
    if (timestamp <= 0)
        return 0;

    if (conversionType == WaveformPlayheadPositionConversionType.NoChange) {
        throw new Error("Unsupported waveform conversion type");
    }

    if (!positions)
        return 0;


    if (conversionType == WaveformPlayheadPositionConversionType.ProcessedToRaw
        || conversionType == WaveformPlayheadPositionConversionType.ProcessedToSensorProcessed) {
        timestamp = InternalMapPoint(positions.processedStart, positions.processedEnd, timestamp, true);
    }
    else if (conversionType == WaveformPlayheadPositionConversionType.SensorProcessedToRaw
        || conversionType == WaveformPlayheadPositionConversionType.SensorProcessedToProcessed) {
        timestamp = InternalMapPoint(positions.sensoredProcessedStart, positions.sensoredProcessedEnd, timestamp, true);
    }

    if (conversionType == WaveformPlayheadPositionConversionType.RawToProcessed
        || conversionType == WaveformPlayheadPositionConversionType.SensorProcessedToProcessed)
        return InternalMapPoint(positions.processedStart, positions.processedEnd, timestamp, false);

    if (conversionType == WaveformPlayheadPositionConversionType.RawToSensorProcessed
        || conversionType == WaveformPlayheadPositionConversionType.ProcessedToSensorProcessed)
        return InternalMapPoint(positions.sensoredProcessedStart, positions.sensoredProcessedEnd, timestamp, false);

    return timestamp;
}

const ResolvePositions = (positions: Positions, waveformType: WaveformType) => {

    if (waveformType == WaveformType.CWFT_PROCESSED) {
        return {
            start: positions.processedStart,
            end: positions.processedEnd
        };
    }
    else if (waveformType == WaveformType.CWFT_SENSOR_PROCESSED) {
        return {
            start: positions.sensoredProcessedStart,
            end: positions.sensoredProcessedEnd
        };
    }
    else
        throw new Error("Unsupported wave form type");
}

export interface HourBasedSegment {
    rawHour: string;
    segmentProcessedChunks: ProcessedChunkDto[]
}
const GetInitialSegments = (rawDuration: number) => {
    const map: { [key: number]: HourBasedSegment } = {};

    for (let hour = 0; hour < rawDuration; hour++) {
        map[hour] = {
            rawHour: `${(hour < 10 ? "0" : "")}${hour}`,
            segmentProcessedChunks: []
        };
    }

    return map;
}
interface PeriodDto {
    rawStartSampled: number;
    rawEndSampled: number;
    processedStartSampled: number;
    processedEndSampled: number;
}

interface ProcessedChunkDto extends PeriodDto {
    durationSampled: number
}

const getPeriod = (rawStartSampled: number, rawEndSampled: number, processedStartSampled: number, processedEndSampled: number): PeriodDto => {

    if (rawStartSampled > rawEndSampled)
        throw new Error(
            `Argument 'rawStartSampled' value '${rawStartSampled}' can not be bigger than the value '${rawEndSampled}' of argument 'rawEndSampled'.`);

    if (processedStartSampled > processedEndSampled)
        throw new Error(
            `Argument 'processedStartSampled' value '${processedStartSampled}' can not be bigger than the value '${processedEndSampled}' of argument 'processedEndSampled'.`);
    return {
        rawStartSampled: rawStartSampled,
        rawEndSampled: rawEndSampled,
        processedStartSampled: processedStartSampled,
        processedEndSampled: processedEndSampled
    }
}

const getProcessedChunck = (rawStartSampled: number, rawEndSampled: number, processedStartSampled: number, processedEndSampled: number) => {
    /// <summary>
    /// Theoretically a processed chunk can have a duration of any length, even a 24h processed chunk or larger,
    /// i.e. the whole raw file could be exactly the same as processed, this limit of 3600s can be changed for future scenarios.
    /// </summary>
    const PROCESSED_CHUNK_DURATION_LIMIT_IN_SEC = 3600;
    if ((processedEndSampled - processedStartSampled) != (rawEndSampled - rawStartSampled))
        throw new Error("Raw and processed duration do not match.");

    const durationSampled = processedEndSampled - processedStartSampled;

    // ASSUMPTION: a processed chunk will never be longer than 1 hour
    if (durationSampled > PROCESSED_CHUNK_DURATION_LIMIT_IN_SEC * SAMPLE_RATE)
        throw new Error("Processed chunk can not have a duration longer than 1 hour (i.e. 3600 seconds).");

    return {
        rawStartSampled: rawStartSampled,
        rawEndSampled: rawEndSampled,
        processedStartSampled: processedStartSampled,
        processedEndSampled: processedEndSampled,
        durationSampled: durationSampled
    }
}


const GetRawHourEndSampled = (hour: number) => hour * SECONDS_IN_HOUR * SAMPLE_RATE - 1;

const GetRawHourStartSampled = (segmentId: number) => segmentId * SECONDS_IN_HOUR * SAMPLE_RATE;

const CreateNextHourChunk = (segments: { [key: number]: HourBasedSegment }, segmentId: number, period: PeriodDto, rawHourEndSampled: number, processedHourEndSampled: number) => {
    const nextSegmentId = segmentId + 1;
    const nextRawHourStartSampled = rawHourEndSampled + 1;
    const nextProcessedHourStartSampled = processedHourEndSampled + 1;

    try {
        segments[nextSegmentId].segmentProcessedChunks.push(
            getProcessedChunck(
                nextRawHourStartSampled,
                period.rawEndSampled,
                nextProcessedHourStartSampled,
                period.processedEndSampled));
    }
    catch (error) {
        throw new Error(`Error while processing an hour segment exceeding chunk for type 'Next Hour Chunk' of next segment ID: '${nextSegmentId}'. 'ProcessedChunkDto' instance arguments were: 'nextRawHourStartSampled' - ${nextRawHourStartSampled}, 'period.RawEndSampled' - ${period.rawEndSampled}, 'nextProcessedHourStartSampled' - ${nextProcessedHourStartSampled}, 'period.ProcessedEndSampled' - ${period.processedEndSampled}`);
    }
}

const CreateCurrentHourChunk = (segments: { [key: number]: HourBasedSegment }, segmentId: number, period: PeriodDto, rawHourEndSampled: number, processedHourEndSampled: number) => {
    try {
        segments[segmentId].segmentProcessedChunks.push(
            getProcessedChunck(
                period.rawStartSampled,
                rawHourEndSampled,
                period.processedStartSampled,
                processedHourEndSampled));
    }
    catch (error) {
        throw new Error(`Error while processing an hour segment exceeding chunk for type 'Current Hour Chunk' of current segment ID: '${segmentId}'. 'ProcessedChunkDto' instance arguments were: 'period.RawStartSampled' - ${period.rawStartSampled}, 'rawHourEndSampled' - ${rawHourEndSampled}, 'period.ProcessedStartSampled' - ${period.processedStartSampled}, 'processedHourEndSampled' - ${processedHourEndSampled}`);
    }
}

/// <summary>
/// Splits a processed chunk between two raw hour segments (because the current
/// chunk in raw time surpasses the ending of the current raw hour)
/// and adds each of the two new chunks to the proper hour segment.
/// Result of <paramref name="processedHourEndSampledFunc"/> represents the splitting point.
/// </summary>
const ProcessExceedingChunk = (
    segments: { [key: number]: HourBasedSegment },
    segmentId: number,
    rawDuration: number,
    period: PeriodDto,
    rawHourEndSampled: number,
    processedHourEndSampledCallBack: () => number) => {
    if (!period == null)
        throw Error('Period is undefined');

    if (period.rawStartSampled > rawDuration * SECONDS_IN_HOUR * SAMPLE_RATE - 1)
        throw Error("The current processed chunk's start time exceeds the duration of the current raw file.");

    const processedHourEndSampled = processedHourEndSampledCallBack();

    // NOTE: (current chunk) from start to the end of current hour:
    CreateCurrentHourChunk(segments, segmentId, period, rawHourEndSampled, processedHourEndSampled);

    // NOTE: (next chunk) from start of next hour to end of current chunk:
    CreateNextHourChunk(segments, segmentId, period, rawHourEndSampled, processedHourEndSampled);
}

/// <summary>
/// Maps the provided <see cref="PeriodDto"/> <paramref name="period"/> sampled data (start and end)
/// to an hour segment. Handles an edge case where a processed chunk can overflow into the next hour segment.
/// </summary>
/// <returns>True if there was an overflow into the next hour segment, else false.</returns>
const MapPeriodToHourSegment = (
    segments: { [key: number]: HourBasedSegment },
    rawDuration: number,
    period: PeriodDto,
    start: number[],
    end: number[]) => {
    if (!segments)
        throw new Error("Segments is undefined");

    if (Object.keys(segments).length != rawDuration)
        throw new Error("The number of processed segments is not equal to the number of hours for the raw file recording.");

    if (!period)
        throw new Error("period is undefined");

    for (let hour = 1; hour <= Object.keys(segments).length; hour++) {
        // 60 sec * 60 min = 1 hr
        //  -> 3600 total sec in a 1 hour seg * 8000 Sample rate
        //  -> 28800000 total 'samples' in 1 hour segment
        const segmentId = (hour - 1);

        const rawHourStartSampled = GetRawHourStartSampled(segmentId);
        const rawHourEndSampled = GetRawHourEndSampled(hour);

        if (period.rawStartSampled > rawHourEndSampled)
            continue;

        if (WithinRange(period.rawStartSampled, rawHourStartSampled, rawHourEndSampled)
            && WithinRange(period.rawEndSampled, rawHourStartSampled, rawHourEndSampled)) {
            segments[segmentId].segmentProcessedChunks.push(getProcessedChunck(period.rawStartSampled, period.rawEndSampled, period.processedStartSampled, period.processedEndSampled));
            break;
        }
        else if (period.rawEndSampled > rawHourEndSampled) {
            ProcessExceedingChunk(
                segments,
                segmentId,
                rawDuration,
                period,
                rawHourEndSampled,
                // NOTE: as we have the raw hour ending, we need to map it to the
                // processed hour ending as that is the splitting point
                () => {
                    return MapSampledPointToProcessed(start, end, rawHourEndSampled);
                });
            return;
        }
    }
}

export function GetHourSegments(positions: Positions, waveformType: WaveformType): IHourBasedSegmentViewModel[] {
    const { start, end } = ResolvePositions(positions, waveformType)

    const rawDuration = positions.rawDuration ? Math.ceil(positions.rawDuration / SECONDS_IN_HOUR) : DEFAULT_RAW_DURATION_HRS;

    const segments = GetInitialSegments(rawDuration);

    let period: PeriodDto = {
        processedEndSampled: 0,
        processedStartSampled: 0,
        rawEndSampled: 0,
        rawStartSampled: 0
    }
    for (let index = 0; index < start.length; index++) {
        const rawStart = start[index];
        const rawEnd = end[index];

        const mappedLength = rawEnd - rawStart;
        const processedStart = period.processedEndSampled + 1;
        const processedEnd = processedStart + mappedLength;

        period = getPeriod(rawStart, rawEnd, processedStart, processedEnd)

        MapPeriodToHourSegment(segments, rawDuration, period, start, end)
    }

    return Object.values(segments).map(entry=> {
       
        return {
            processedStartTime: entry.segmentProcessedChunks.length > 0 ? SampleToSecond(entry.segmentProcessedChunks[0].processedStartSampled) : 0,
            processedEndTime: entry.segmentProcessedChunks.length > 0 ? SampleToSecond(entry.segmentProcessedChunks[entry.segmentProcessedChunks.length - 1].processedEndSampled) : 0,
            rawHour: entry.rawHour
        }
    });
}
